<template>
  <div id="place-edit-page">
    <!-- Content-->
    <div class="vx-row w-full">
      <div class="vx-col w-full md:w-1/2">
        <vs-input v-model="place.alias" :label="$t('places.alias')" class="mt-5 w-full" name="new-alias" />
        <span class="text-danger text-sm" v-show="errors.has('new-alias')">{{
          errors.first('new-alias')
        }}</span>
      </div>
    </div>
    <div class="vx-row w-full mt-5">
      <div class="vx-col w-full lg:w-1/4 md:w-1/2">
        <label class="vs-input--label">{{ $t('places.category') }}</label>
        <v-select
          v-model="place.category"
          :clearable="false"
          :options="categoryOptions"
          :reduce="option => option.value"
          name="new-category"
          v-validate="'required'"
          :data-vv-as="$t('places.category')"
          :dir="$vs.rtl ? 'rtl' : 'ltr'"
        />
        <span class="text-danger text-sm" v-show="errors.has('new-category')">{{
          errors.first('new-category')
        }}</span>
      </div>
      <div class="vx-col w-full lg:w-1/4 md:w-1/2">
        <label class="vs-input--label">{{ $t('places.subject') }}</label>
        <v-select
          v-model="place.subject"
          :clearable="false"
          :options="subjectOptions"
          :reduce="option => option.value"
          name="new-subject"
          v-validate="'required'"
          :data-vv-as="$t('places.subject')"
          :dir="$vs.rtl ? 'rtl' : 'ltr'"
        />
        <span class="text-danger text-sm" v-show="errors.has('new-subject')">{{
          errors.first('new-subject')
        }}</span>
      </div>
      <div class="vx-col w-full lg:w-1/4 md:w-1/2" v-if="canChangeState">
        <label class="vs-input--label">{{ $t('entity.state') }}</label>
        <v-select
          v-model="place.state"
          :clearable="false"
          :options="stateOptions"
          :reduce="option => option.value"
          name="new-state"
          v-validate="'required'"
          :data-vv-as="$t('places.state')"
          :dir="$vs.rtl ? 'rtl' : 'ltr'"
        />
        <span class="text-danger text-sm" v-show="errors.has('new-state')">{{
          errors.first('new-state')
        }}</span>
      </div>
      <div class="vx-col w-full lg:w-1/4 md:w-1/2">
        <label class="vs-input--label">{{ $t('places.status') }}</label>
        <v-select
          v-model="place.status"
          :clearable="false"
          :options="statusOptions"
          :reduce="option => option.value"
          name="new-status"
          v-validate="'required'"
          :data-vv-as="$t('places.status')"
          :dir="$vs.rtl ? 'rtl' : 'ltr'"
        />
        <span class="text-danger text-sm" v-show="errors.has('new-status')">{{
          errors.first('new-status')
        }}</span>
      </div>
      <div class="vx-col w-full lg:w-1/4 md:w-1/2">
        <label class="vs-input--label">{{ $t('places.employee_percent') }}</label>
        <vs-input
          v-model="place.employeePercent"
          @keyup="place.employeePercent = translateNumbers(place.employeePercent)"
          type="number"
          class="w-full"
          name="new-employee-percent"
          v-validate="'required|percent'"
          :data-vv-as="$t('places.employee_percent')"
          :dir="$vs.rtl ? 'rtl' : 'ltr'"
        />
        <span class="text-danger text-sm" v-show="errors.has('new-employee-percent')">{{
          errors.first('new-employee-percent')
        }}</span>
      </div>
    </div>
    <div class="vx-row w-full mt-5">
      <p class="vx-col">
        <address-string :address="addressString" />
      </p>
    </div>

    <open-street-map v-model="mapData" class="m-3" :markers="placeMarker" :disable-fit="!placeLocation" />

    <div class="vx-row w-full mt-3">
      <div class="vx-col w-full">
        <upload-file
          v-model="place.attachments"
          multiple
          ref="uploads"
          :start-upload="startUpload"
          :title="$t('places.attachments')"
          @pending-upload="handlePendingUpload"
        >
          <download-files class="mb-base" :files="place.attachments" />
        </upload-file>
      </div>
      <div class="vx-col w-full">
        <div class="mt-8 flex flex-wrap items-center">
          <vs-textarea
            v-model="place.description"
            :label="$t('places.description')"
            class="mt-5 w-full textarea-h-350"
            name="new-description"
          />
          <span class="text-danger text-sm" v-show="errors.has('new-description')">{{
            errors.first('new-description')
          }}</span>
        </div>
      </div>
    </div>
    <div class="vx-row w-full">
      <div class="vx-col w-full">
        <div class="mt-8 flex flex-wrap items-center">
          <div class="ml-auto">
            <vs-button class="mt-2 mx-2" type="border" color="danger" @click="openDeleteConfirm">
              {{ $t('forms.delete') }}
            </vs-button>
            <vs-button
              class="ml-auto mt-2 vs-con-loading__container"
              id="save_btn"
              color="success"
              @click="saveChanges"
              :disabled="!validForm || disabled_btn"
            >
              {{ canSubmit ? $t('forms.update') : $t('upload_files.upload_file') }}
            </vs-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import modulePlaces from '@/store/places/modulePlaces'
import OpenStreetMap from '@/views/components/maps/OpenStreetMap'
import AddressString from '@/views/components/maps/AddressString'
import UploadFile from '@/views/components/forms/UploadFile'
import DownloadFiles from '@/views/components/forms/DownloadFiles'
import { TranslateNumbers } from '@/utils'

export default {
  name: 'PlaceEdit',
  components: {
    DownloadFiles,
    UploadFile,
    OpenStreetMap,
    AddressString
  },
  data() {
    return {
      place: {},
      mapData: {},
      placeLocation: true,
      categorySubjects: {},
      startUpload: false,
      canSubmit: true,
      disabled_btn: false
    }
  },
  computed: {
    canChangeState() {
      return (
        this.$store.state.AppActiveUser.roles.includes('system') ||
        this.$store.state.AppActiveUser.roles.includes('admin')
      )
    },
    validForm() {
      return this.place.alias && this.place.employeePercent && this.mapData.opStatus === 'loaded'
    },
    subjectOptions() {
      const values = this.categorySubjects[this.place.category]
      const options = []
      if (values) {
        for (const r in values) {
          options.push({
            label: this.$t(`places.subjects.${values[r]}`),
            value: values[r]
          })
        }
      }
      return options
    },
    categoryOptions() {
      return this.$store.getters['enum/selectOptions']('PlaceCategory', 'places.categories')
    },
    stateOptions() {
      return this.$store.getters['enum/selectOptions']('State', 'entity.states')
    },
    statusOptions() {
      return this.$store.getters['enum/selectOptions']('PlaceStatus', 'places.statuses')
    },
    placeMarker() {
      return this.place.location
        ? !this.placeLocation
          ? []
          : [{ latlng: [this.place.location.lat, this.place.location.lon] }]
        : []
    },
    addressString() {
      return !this.placeLocation ? this.mapData : this.place
    }
  },
  watch: {
    mapData(value) {
      if (value.opStatus === 'loaded') {
        this.placeLocation = false
      }
    },
    'place.category'() {
      this.place.subject = this.subjectOptions[0].value
    }
  },
  methods: {
    updatePlace() {
      this.$store.dispatch('place/fetchPlace', this.$route.params.placeId).then(res => {
        this.place = Object.assign({}, res.data.place)
        this.placeLocation = true
      })
    },
    handlePendingUpload(value) {
      this.canSubmit = !value
    },
    saveChanges() {
      this.$validator.validateAll().then(result => {
        if (!this.canSubmit) this.startUpload = true
        else if (result) {
          this.disabled_btn = true
          this.$vs.loading({
            background: 'success',
            container: '#save_btn',
            color: '#fff',
            scale: 0.45
          })

          const data = Object.assign(this.place, this.mapData)
          this.$store
            .dispatch('place/editPlace', { id: this.place.id, data })
            .then(res => {
              this.$vs.notify({
                title: this.$t('forms.successfully_edited'),
                iconPack: 'feather',
                icon: 'icon-alert-circle',
                color: 'success'
              })
              if (this.canChangeState) {
                this.$store.dispatch('place/editPlaceState', { id: res.data.id, state: this.place.state })
              }
              this.$emit('done', res)
            })
            .finally(() => {
              this.$vs.loading.close('#save_btn > .con-vs-loading')
              this.disabled_btn = false
            })
        }
      })
    },
    openDeleteConfirm() {
      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: this.$t('button.delete'),
        text: this.$t('messages.are_you_sure'),
        accept: () => {
          this.$store.dispatch('place/removePlace', this.$route.params.placeId).then(() => {
            this.$router.push({ name: 'business' })
          })
        },
        acceptText: this.$t('button.delete'),
        cancelText: this.$t('button.cancel')
      })
    },
    translateNumbers(val) {
      return TranslateNumbers(val)
    }
  },
  created() {
    this.$vs.loading()
    if (!modulePlaces.isRegistered) {
      this.$store.registerModule('place', modulePlaces)
      modulePlaces.isRegistered = true
    }
    this.updatePlace()
    this.$store
      .dispatch('config/fetchConfig', 'Place:Category:Subject')
      .then(res => {
        this.categorySubjects = res.data.value
        this.mapData.opStatus = 'loaded'
      })
      .finally(() => this.$vs.loading.close())
  }
}
</script>

<style lang="scss">
#place-edit-page .vs__dropdown-menu {
  z-index: 9000;
}
.textarea-h-350 {
  height: 350px;
  textarea {
    height: 330px;
  }
}
</style>
